import { normalizeAddressRegionCode } from '@/lib/address-region-options'
import type { Address } from '@/lib/services/addresses/addresses.types'
import type { PlaceAddressComponents } from '@/hooks/use-places-autocomplete'
import type { CustomerAddressInput } from './customers.types'

/**
 * A customer address as the forms edit it. Drafts start from `emptyCustomerAddressDraft` and are
 * filled from saved `Address` rows, so text fields the API leaves null stay null.
 */
export interface CustomerAddressDraft {
  name: string | null
  line1: string | null
  line2: string | null
  city: string | null
  state: string
  postal_code: string | null
  postal_code_suffix: string | null
  country: string
  timezone: string
  lat: number | string | null
  lng: number | string | null
  google_place_id: string
  is_verified: boolean
  is_rooftop: boolean
}

/** A saved address or a form draft; the helpers below read either shape. */
export type CustomerAddressSource = Partial<Address> | Partial<CustomerAddressDraft>

/** Place details from `usePlacesAutocomplete().getPlaceDetails()`. */
export interface CustomerAddressPlace extends PlaceAddressComponents {
  timezone?: string
  lat?: number
  lng?: number
  placeId?: string
  is_rooftop?: boolean
}

/**
 * `normalizeAddressRegionCode` always returns a string, but its JavaScript source is untyped, so
 * its result is narrowed here.
 */
function normalizeRegionCode(value: string | null | undefined): string {
  const regionCode: unknown = normalizeAddressRegionCode(value)

  return typeof regionCode === 'string' ? regionCode : ''
}

/** The slice of a TanStack form these helpers write through. */
export interface CustomerAddressForm {
  setFieldValue(field: string, value: unknown): void
}

export const emptyCustomerAddressDraft: Readonly<CustomerAddressDraft> = Object.freeze({
  name: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  postal_code_suffix: null,
  country: 'US',
  timezone: '',
  lat: null,
  lng: null,
  google_place_id: '',
  is_verified: false,
  is_rooftop: false,
})

export function customerAddressDraftFrom(
  address: CustomerAddressSource | null | undefined
): CustomerAddressDraft {
  return {
    ...emptyCustomerAddressDraft,
    ...(address || {}),
    country: address?.country || emptyCustomerAddressDraft.country,
    state: normalizeRegionCode(address?.state),
    timezone: address?.timezone || '',
    lat: address?.lat ?? null,
    lng: address?.lng ?? null,
    google_place_id: address?.google_place_id || '',
    is_verified: Boolean(address?.is_verified),
    is_rooftop: Boolean(address?.is_rooftop),
  }
}

export function customerAddressHasValue(
  address: CustomerAddressSource | null | undefined
): boolean {
  return (['line1', 'city', 'postal_code'] as const).some((field) =>
    String(address?.[field] ?? '').trim()
  )
}

/** The address fields `formatCustomerAddressLines` reads. */
export interface CustomerAddressLineSource {
  line1?: string | null
  line2?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
}

function isPresentLine(line: string | null | undefined): line is string {
  return Boolean(line)
}

export function formatCustomerAddressLines(
  address: CustomerAddressLineSource | null | undefined,
  {
    includeCountry = false,
    combineStreetLines = false,
  }: { includeCountry?: boolean; combineStreetLines?: boolean } = {}
): string[] {
  if (!address) {
    return []
  }

  const cityLine = [[address.city, address.state].filter(Boolean).join(', '), address.postal_code]
    .filter(Boolean)
    .join(' ')
    .trim()

  const streetLine = combineStreetLines
    ? [address.line1, address.line2].filter(Boolean).join(', ')
    : address.line1

  return [
    streetLine,
    !combineStreetLines ? address.line2 : null,
    cityLine,
    includeCountry && address.country && address.country !== 'US' ? address.country : null,
  ].filter(isPresentLine)
}

export function formatCustomerAddressLine(
  address: CustomerAddressLineSource | null | undefined
): string | null {
  const lines = formatCustomerAddressLines(address)

  return lines.length ? lines.join(', ') : null
}

function trimmedString(value: string | null | undefined) {
  return String(value ?? '').trim()
}

export function cleanCustomerAddressDraft(
  address: CustomerAddressSource | null | undefined
): CustomerAddressInput | null {
  // `customerAddressHasValue` is false for a missing address; the explicit check narrows it.
  if (!address || !customerAddressHasValue(address)) {
    return null
  }

  return {
    name: trimmedString(address.name) || null,
    line1: trimmedString(address.line1),
    line2: trimmedString(address.line2) || null,
    city: trimmedString(address.city),
    state: normalizeRegionCode(address.state),
    postal_code: trimmedString(address.postal_code),
    postal_code_suffix: address.postal_code_suffix || null,
    country: trimmedString(address.country) || emptyCustomerAddressDraft.country,
    timezone: trimmedString(address.timezone) || null,
    lat: address.lat === '' ? null : (address.lat ?? null),
    lng: address.lng === '' ? null : (address.lng ?? null),
    google_place_id: trimmedString(address.google_place_id) || null,
    is_verified: Boolean(address.is_verified),
    is_rooftop: Boolean(address.is_rooftop),
  }
}

export function clearCustomerAddressVerification(form: CustomerAddressForm, fieldPrefix: string) {
  form.setFieldValue(`${fieldPrefix}.postal_code_suffix`, null)
  form.setFieldValue(`${fieldPrefix}.lat`, null)
  form.setFieldValue(`${fieldPrefix}.lng`, null)
  form.setFieldValue(`${fieldPrefix}.google_place_id`, '')
  form.setFieldValue(`${fieldPrefix}.is_verified`, false)
  form.setFieldValue(`${fieldPrefix}.is_rooftop`, false)
}

export function applyCustomerAddressPlace(
  form: CustomerAddressForm,
  fieldPrefix: string,
  place: CustomerAddressPlace,
  { isRooftop = null }: { isRooftop?: boolean | null } = {}
) {
  const streetNumber = place.street_number ?? ''
  const route = place.route ?? ''
  const line1 = [streetNumber, route].filter(Boolean).join(' ').trim()

  form.setFieldValue(`${fieldPrefix}.line1`, line1)
  form.setFieldValue(`${fieldPrefix}.line2`, place.subpremise ?? '')
  form.setFieldValue(`${fieldPrefix}.city`, place.locality ?? place.neighborhood ?? '')
  form.setFieldValue(`${fieldPrefix}.state`, normalizeRegionCode(place.administrative_area_level_1))
  form.setFieldValue(`${fieldPrefix}.postal_code`, place.postal_code ?? '')
  form.setFieldValue(`${fieldPrefix}.postal_code_suffix`, place.postal_code_suffix || null)
  form.setFieldValue(`${fieldPrefix}.country`, place.country ?? emptyCustomerAddressDraft.country)
  form.setFieldValue(`${fieldPrefix}.timezone`, place.timezone ?? '')
  form.setFieldValue(`${fieldPrefix}.lat`, place.lat ?? null)
  form.setFieldValue(`${fieldPrefix}.lng`, place.lng ?? null)
  form.setFieldValue(`${fieldPrefix}.google_place_id`, place.placeId ?? '')
  form.setFieldValue(`${fieldPrefix}.is_verified`, Boolean(place.placeId))
  form.setFieldValue(
    `${fieldPrefix}.is_rooftop`,
    isRooftop === null ? Boolean(place.is_rooftop) : Boolean(isRooftop)
  )
}
